/**
 * Unified editing state colors for shift management
 * Uses primary color with opacity to reduce brightness
 *
 * This provides a single source of truth for all editing-related styling:
 * - Day selection
 * - Inline shift creation overlay
 * - Shift resize handles
 * - Shift dragging indication
 * - Shift selection
 * - Day template drop zones
 */

// Tailwind class versions
export const EDITING_COLORS = {
  // Backgrounds (layered from subtle to prominent)
  BG_SUBTLE: 'bg-primary/15 dark:bg-primary/20',      // Day selection overlay
  BG_LIGHT: 'bg-primary/30 dark:bg-primary/35',       // Dragging state, drop zones
  BG_MEDIUM: 'bg-primary/40 dark:bg-primary/45',      // Creation preview indicator
  BG_SELECTED: 'bg-primary/50 dark:bg-primary/55',    // Shift selection - visible but not overwhelming
  BG_PROMINENT: 'bg-primary/85 dark:bg-primary/90',   // Tooltips, solid backgrounds
  BG_SOLID: 'bg-primary dark:bg-primary',             // Day number when editing

  // Borders
  BORDER_SUBTLE: 'border-primary/50',                   // Subtle indication
  BORDER_MEDIUM: 'border-primary/70',                   // Creation overlay border
  BORDER_SOLID: 'border-primary',                       // Solid borders

  // Rings (for focus/selection)
  RING_SUBTLE: 'ring-primary/60',                       // Dragging state
  RING_SOLID: 'ring-primary',                           // Selected state

  // Text
  TEXT_ON_DARK: 'text-white',                           // Text on prominent/solid bg
  TEXT_COLORED: 'text-primary',                         // Colored text on light bg

  // Interactive hover states
  HOVER_PROMINENT: 'hover:bg-primary/75',               // Hover on prominent bg
  HOVER_LIGHT: 'hover:bg-primary/40',                   // Hover on light bg
} as const;

// CSS variable versions for inline styles (modern rgb() syntax with space-separated values)
export const EDITING_COLORS_CSS = {
  BG_SUBTLE: 'rgb(var(--primary-rgb) / 0.15)',
  BG_LIGHT: 'rgb(var(--primary-rgb) / 0.3)',
  BG_MEDIUM: 'rgb(var(--primary-rgb) / 0.4)',
  BG_OVERLAY: 'rgb(var(--primary-rgb) / 0.2)',          // For overlay backgrounds
  BG_PROMINENT: 'rgb(var(--primary-rgb) / 0.85)',       // For tooltips
  BORDER_SUBTLE: 'rgb(var(--primary-rgb) / 0.5)',
  BORDER_MEDIUM: 'rgb(var(--primary-rgb) / 0.7)',
} as const;

// Composite style presets for common use cases
export const EDITING_PRESETS = {
  // Tooltip styling (replaces teal-700)
  TOOLTIP: `${EDITING_COLORS.BG_PROMINENT} ${EDITING_COLORS.TEXT_ON_DARK} border ${EDITING_COLORS.BORDER_MEDIUM} ${EDITING_COLORS.HOVER_PROMINENT}`,

  // Selection ring + tint
  SELECTED: `ring-2 ${EDITING_COLORS.RING_SOLID} ${EDITING_COLORS.BG_SUBTLE}`,

  // Dragging indication
  DRAGGING: `${EDITING_COLORS.BG_LIGHT} ${EDITING_COLORS.BORDER_SOLID} ${EDITING_COLORS.RING_SUBTLE}`,

  // Day editing state
  DAY_EDITING: `${EDITING_COLORS.BG_SOLID} ${EDITING_COLORS.TEXT_ON_DARK}`,

  // Drop zone active
  DROP_ZONE: `${EDITING_COLORS.BG_LIGHT} border-2 ${EDITING_COLORS.BORDER_SOLID}`,
} as const;
